import { isArray, isEmpty, reduce, find, map, filter } from "lodash";
import { isBrowser, createURL } from "~/utilities/helpers";
import { navigate } from "gatsby";

const SEPARATOR = "|";

const createArray = (string) => {
    if (!string) {
        return [];
    }

    return string.split(SEPARATOR);
};

const formatPageSize = (paramPageSize) => {
    const validSizes = [25, 50, 100, 150, 200];
    let pageSize = 25;

    if (validSizes.includes(paramPageSize)) {
        pageSize = paramPageSize;
    }
    return pageSize;
};

const parseIntParams = (string, base, paramDefault) => {
    const parsed = parseInt(string, base);
    if (isNaN(parsed)) {
        return paramDefault;
    }
    return parsed;
};

const getPageParams = (getQueryParams) => {
    const initialQuery = getQueryParams("q");
    const initialPageSize = parseIntParams(getQueryParams("size"), 10, 25);
    const initialPageIndex = parseIntParams(getQueryParams("page"), 10, 1);
    return {
        initialQuery,
        initialPageIndex: initialPageIndex - 1,
        initialPageSize: formatPageSize(initialPageSize),
    };
};

export const getUrlFromElasticsearch = (elasticsearchDocument, getBasePath) => {
    const parsePageUrl = (url, post_lang) => {
        const href = url.replace(process.env.GATSBY_ADMIN_SITE_URL, "");
        const queryStringIndex = href.indexOf("?");
        const end = queryStringIndex > -1 ? queryStringIndex : href.length;
        const hrefNoQueryString = href.slice(0, end);
        const code = post_lang === "en" ? "" : post_lang;
        return `${hrefNoQueryString}${code}`;
    };

    const parseSlug = (slug, postLang) => {
        if (postLang === "zh-tw") {
            return slug.replace("/cn", "/zh-tw");
        }

        if (postLang === "zh-cn") {
            return slug.replace("/zh-hans", "/zh-cn");
        }

        return slug;
    };

    const parseUrl = (url) => {
        return url.replace(process.env.GATSBY_ADMIN_SITE_URL, "");
    };

    const PATHS_MAPPINGS = {
        "knowledge-center": "knowledgeCenterArticle",
        news: "newsBase",
        events: "eventsBase",
        al_product: "productsBase",
        page: "pagesBase",
        dlm_download: "documentsBase",
    };

    const {
        post_name: slug,
        permalink,
        post_type: postType,
        post_lang,
        meta,
    } = elasticsearchDocument || {};

    if (postType === "page") {
        const url = createURL(permalink);
        return parsePageUrl(url.href, post_lang);
    }

    const urlOverride =
        find(meta?.url_override, ({ value }) => value)?.value || "";

    if (postType === "knowledge-center" && urlOverride) {
        return urlOverride;
    }

    const parsedSlug = parseSlug(slug, post_lang);
    const baseUrl = getBasePath(PATHS_MAPPINGS[postType])?.url || "";

    return parseUrl(`${baseUrl}${parsedSlug}/`);
};

export const getTypeLabel = (post_type) => {
    switch (post_type) {
        case "dlm_download:vnd.openxmlformats-officedocument.presentationml.slideshow":
            return "Presentations";
        case "dlm_download:pdf":
            return "PDF Files";
        case "dlm_download:zip":
            return "ZIP Archives";
        case "dlm_download:txt":
            return "Text Files";
        case "dlm_download:video":
            return "Videos";
        case "dlm_download:ppsx":
            return "PowerPoint Files";
        case "attachment:pdf":
            return "PDF Files";
        case "attachment:zip":
            return "ZIP Archives";
        case "attachment:txt":
            return "Text Files";
        case "attachment:video":
            return "Videos";
        case "attachment:ppsx":
            return "PowerPoint Files";
        case "al_product":
            return "Products";
        case "page":
            return "Web Pages";
        case "knowledge-center":
            return "Knowledge Center";
        case "news":
            return "News";
        case "events":
            return "Events";
        case "dealers":
            return "Dealers";
        default:
            if (post_type.includes("dlm_download")) {
                const fileType = post_type.split(":")[1];
                if (fileType) {
                    return `${fileType.toUpperCase()} Files`;
                }
                return "Document Library";
            }
            if (post_type.includes("attachment")) {
                const fileType = post_type.split(":")[1];
                if (fileType) {
                    return `${fileType.toUpperCase()} Files`;
                }
                return "Attachments";
            }
            return post_type;
    }
};

export const formatDefaultFilters = (defaultFilters) => {
    return reduce(
        defaultFilters,
        (acc, value, key) => {
            if (key === "productLine") {
                return { ...acc, [key]: value?.slug || "" };
            }
            return {
                ...acc,
                [key]: value ? value.map(({ slug }) => slug) : [],
            };
        },
        {},
    );
};

export const setIsActive = (categories, productType) => {
    const options = [
        {
            active: true,
            slug: "view-all",
            disabled: false,
            name: "View All",
            id: "view-all",
        },
    ];

    categories?.forEach((categoryOption) => {
        const isActive = productType === categoryOption.slug;
        if (isActive) {
            const viewAllOption = options[0];
            viewAllOption.active = false;
        }
        options.push({ ...categoryOption, active: isActive });
    });

    return options;
};

export const createParams = (filters) => {
    const params = {};

    for (const filter in filters) {
        const value = filters[filter];
        if (typeof value === "number" || !isEmpty(value)) {
            if (isArray(value)) {
                params[filter] = value.join(SEPARATOR);
            } else {
                params[filter] = value;
            }
        }
    }

    return params;
};

export const updateUrl = (params, origin, pathname) => {
    if (isBrowser) {
        const url = createURL(`${origin}${pathname}`);
        for (const param in params) {
            url.searchParams.set(param, params[param]);
        }

        navigate(url.search, {
            state: { disableScrollUpdate: true },
        });
    }
};

export const getUrl = (params, origin, pathname) => {
    if (isBrowser) {
        const url = createURL(`${origin}${pathname}`);
        for (const param in params) {
            url.searchParams.set(param, params[param]);
        }
        return url;
    }

    return null;
};

export const hideFilters = (filters, valuesToHide) => {
    const filtersToHide = map(valuesToHide, "slug");
    return map(filters, (documentFilter) => {
        if (documentFilter.type === "productLine") {
            return {
                ...documentFilter,
                values: filter(documentFilter.values, (productLine) => {
                    return !filtersToHide.includes(productLine.slug);
                }),
            };
        }
        return documentFilter;
    });
};

export const formatSearchParams = (getQueryParams) => {
    const initialProductLine = getQueryParams("productLine");
    const initialTopics = getQueryParams("topics");
    const initialTypes = getQueryParams("types");
    const initialDocumentType = getQueryParams("documentType");
    const initialLanguages = getQueryParams("languages");
    const pageParams = getPageParams(getQueryParams);

    return {
        query: pageParams.initialQuery,
        size: pageParams.initialPageSize,
        pageIndex: pageParams.initialPageIndex,
        productLine: initialProductLine,
        topics: createArray(initialTopics),
        documentType: createArray(initialDocumentType),
        types: createArray(initialTypes),
        languages: createArray(initialLanguages),
    };
};

export const formatDocumentSearchParams = (getQueryParams) => {
    const initialProductLine = getQueryParams("productLine");
    const initialTopics = getQueryParams("topics");
    const initialProductCategory = getQueryParams("productCategory");
    const initialFormat = getQueryParams("format");
    const initialDocumentType = getQueryParams("documentType");
    const sortByTitle = getQueryParams("sortByTitle");
    const sortByType = getQueryParams("sortByType");
    const pageParams = getPageParams(getQueryParams);

    return {
        query: pageParams.initialQuery,
        size: pageParams.initialPageSize,
        pageIndex: pageParams.initialPageIndex,
        productLine: initialProductLine,
        topics: createArray(initialTopics),
        productCategory: createArray(initialProductCategory),
        format: createArray(initialFormat),
        documentType: createArray(initialDocumentType),
        sortByTitle,
        sortByType,
    };
};
